interface SectionHeadingProps {
  id: string;
  title: string;
  lead?: string;
  /** Short translated label shown in the capsule above the title. */
  eyebrow?: string;
  tone?: "light" | "night";
  /** Centred is the landing default; "left" keeps the header in a column. */
  align?: "center" | "left";
  className?: string;
}

/**
 * Section header in the orbit language: a floating capsule with a lit dot, a
 * display headline and a measured lead, resting on a soft arc of light.
 */
export default function SectionHeading({
  id,
  title,
  lead,
  eyebrow,
  tone = "light",
  align = "center",
  className = "",
}: SectionHeadingProps) {
  const isNight = tone === "night";
  const isCentered = align === "center";

  // Night headings sit on a dark section with light text, so their glass
  // takes the dark tokens instead of a white wash.
  return (
    <div
      data-theme={isNight ? "dark" : undefined}
      className={`relative ${isCentered ? "flex flex-col items-center" : ""} ${className}`}
    >
      {eyebrow && (
        <p
          className={`glass-pill mb-6 inline-flex items-center gap-2.5 py-2 pl-2.5 pr-5 text-[13px] font-semibold ${
            isNight ? "text-white" : "text-signal-deep"
          }`}
        >
          <span
            aria-hidden="true"
            className="relative flex h-5 w-5 items-center justify-center"
          >
            <span className="absolute inset-0 rounded-full bg-signal-accent/20 animate-signal-pulse motion-reduce:animate-none" />
            <span className="h-2 w-2 rounded-full bg-signal-accent" />
          </span>
          {eyebrow}
        </p>
      )}
      <h2
        id={id}
        className={`max-w-[22ch] text-balance font-display text-[clamp(2rem,3.8vw,3.5rem)] font-semibold leading-[1.05] tracking-[-0.035em] ${
          isCentered ? "text-center" : ""
        } ${isNight ? "text-white" : "text-signal-ink"}`}
      >
        {title}
      </h2>
      <span
        aria-hidden="true"
        className={`mt-6 block h-1 w-24 rounded-full bg-signal-accent ${
          isNight ? "opacity-70" : ""
        }`}
      />
      {lead && (
        <p
          className={`mt-6 max-w-[62ch] text-pretty text-[17px] leading-[1.65] ${
            isCentered ? "text-center" : ""
          } ${isNight ? "text-signal-nightMuted" : "text-signal-muted"}`}
        >
          {lead}
        </p>
      )}
    </div>
  );
}
